import { AnimatedButton } from '@dtx-company/shared-components/src/components/atoms/AnimatedButton/index'
import { Box, BoxProps, Checkbox, FormControlLabel } from '@mui/material'
import { Coupon } from '@dtx-company/true-common/src/types/Coupon'
import { PPESummaryTopSection } from './PPESummaryTopSection'
import {
  Plan,
  PlanExtraDetail,
  PlanExtraRender,
  PlanPurchaseExperienceMode
} from '@dtx-company/true-common/src/types/Plan'
import {
  PlanCostSummary,
  PlanCostSummaryType
} from '@dtx-company/inter-app/src/types/PlanCostSummary'
import { PlanExtrasForm } from '../../../ProFlex/AddOns'
import { ReactNode, useMemo, useState } from 'react'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { _SummaryLineParagraph } from './PPESummarySection.styles'
import { css } from '@emotion/react'
import { formatDate } from '@app/code/src/utils/my-flowcodes'
import { getPlanTypeDirection } from '../../../../utils/getPlanTypeDirection'
import { getPriceText } from '../../../../utils/getPriceText'
import { isPilotPlanType } from '@app/common/src/utils/isPilotPlanType'
import Link from 'next/link'
import styled from '@emotion/styled'
import theme from '../../../../theme'

export interface PPESummarySectionProps {
  selectedPlan: Plan
  currentPlan?: Plan
  promo: Partial<Coupon>
  planCostSummary: PlanCostSummary | undefined
  currentPlanExtraDetails?: PlanExtraDetail[]
  planExtraFormValues: PlanExtrasForm
  planExtraRender?: PlanExtraRender[]
  isProcessing: boolean
  isFormValid: boolean
  stickyBottom: number | undefined
  isTotalFloating: boolean
  showChangeAddons: boolean
  mode?: PlanPurchaseExperienceMode
  hasDowngradeError?: boolean
}

export function PPESummarySection(props: PPESummarySectionProps): JSX.Element {
  const isCurrentPlanSelected = props.selectedPlan.id === props.currentPlan?.id
  const isPilot = props.currentPlan ? isPilotPlanType(props.currentPlan.type) : false
  const isDowngrade =
    !isPilot && props.currentPlan?.type && props.selectedPlan.type
      ? getPlanTypeDirection(props.currentPlan?.type, props.selectedPlan.type) < 0
      : false
  const [isConfirmed, setIsConfirmed] = useState(false)
  const isSubmitDisabled =
    (isCurrentPlanSelected &&
      props.mode !== PlanPurchaseExperienceMode.CHANGE_PAYMENT &&
      !props.showChangeAddons) ||
    !props.isFormValid ||
    (isDowngrade ? !isConfirmed : false)

  const grandTotalPriceText = (() => {
    if (props.planCostSummary) {
      if (props.planCostSummary.grandTotalInCents !== undefined) {
        return getPriceText({ priceInCents: props.planCostSummary.grandTotalInCents })
      } else {
        // Shouldn't happen but technically possible
        return ''
      }
    }
    return 'Calculating...'
  })()

  const isCredit = (props.planCostSummary?.grandTotalInCents || 0) < 0
  const submitButtonLabel = useMemo(() => {
    if (props.mode === PlanPurchaseExperienceMode.CHANGE_PAYMENT) {
      return 'Update payment'
    }
    if (isCurrentPlanSelected && !props.showChangeAddons) {
      return 'Already on this plan'
    }
    return 'Complete Checkout'
  }, [props.mode, props.showChangeAddons, isCurrentPlanSelected])

  const dueDateText = useMemo(() => {
    if (isCredit) return ''
    if (props.planCostSummary?.downgradePeriodEndDate) {
      return `due ${formatDate(props.planCostSummary.downgradePeriodEndDate)}`
    }
    if (props.mode === PlanPurchaseExperienceMode.CHANGE_PAYMENT) {
      return 'due on your next billing date'
    }
    return 'due today'
  }, [props.mode, isCredit, props.planCostSummary?.downgradePeriodEndDate])

  const grandTotalLabelText = useMemo(() => {
    if (props.mode === PlanPurchaseExperienceMode.CHANGE_PAYMENT) return 'New Total'
    if (
      props.mode === PlanPurchaseExperienceMode.PURCHASE_ADDONS &&
      (!props.planCostSummary?.type || props.planCostSummary.type === PlanCostSummaryType.SAME)
    ) {
      return ''
    }
    return 'Total'
  }, [props.mode, props.planCostSummary?.type])

  const grandTotalLineText = useMemo(() => {
    if (!grandTotalLabelText) return ''
    if (dueDateText) {
      return `${grandTotalLabelText} (${dueDateText})`
    }
    return grandTotalLabelText
  }, [grandTotalLabelText, dueDateText])

  if (props.hasDowngradeError) return <_PlanDowngradeErrorAlert />

  return (
    <>
      <PPESummaryTopSection
        selectedPlan={props.selectedPlan}
        mode={props.mode}
        promo={props.promo}
        planCostSummary={props.planCostSummary}
        currentPlanExtraDetails={props.currentPlanExtraDetails}
        planExtraFormValues={props.planExtraFormValues}
        planExtraRender={props.planExtraRender}
      />
      <_TotalBox stickyBottom={props.stickyBottom} floating={props.isTotalFloating}>
        <_TotalContentBox>
          {grandTotalLineText && (
            <>
              <_SummaryLineParagraph>
                <_SummaryLineText color={theme.colors.primary.black}>
                  {grandTotalLineText}
                </_SummaryLineText>
                <_SummaryLineText
                  fontSize="20px"
                  color={isCredit ? theme.colors.status.successDark : theme.colors.primary.black}
                >
                  {grandTotalPriceText}
                </_SummaryLineText>
              </_SummaryLineParagraph>
              <Text
                component="p"
                variant="body/medium"
                color={theme.colors.primary.grey}
                textAlign="right"
              >
                plus applicable sales tax
              </Text>
            </>
          )}
          {isCredit && (
            <_SummaryLineParagraph style={{ marginTop: '4px' }}>
              <Text component="span" variant="body/small">
                This will show up as a credit on your account and be applied to your next billing
                cycle.
              </Text>
            </_SummaryLineParagraph>
          )}
          {isDowngrade && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isConfirmed}
                  onChange={(_event, checked) => setIsConfirmed(checked)}
                  sx={{ mb: 'auto' }}
                />
              }
              label={
                <Text component="span" variant="body/small" color={theme.colors.primary.grey}>
                  I understand that my subscription will change and that from now on my most recent
                  codes and pages that fit within my subscription limit will be saved.
                </Text>
              }
              sx={{ mt: '24px' }}
            ></FormControlLabel>
          )}
          <AnimatedButton
            type="submit"
            textVariant="button/general"
            sizeVariant="mobileFullWidth"
            label={submitButtonLabel}
            loadingProps={{ label: 'Processing...', width: 22, height: 22, marginTop: 11 }}
            loading={props.isProcessing}
            disabled={isSubmitDisabled}
            style={{ marginTop: '24px', width: '100%' }}
          />
        </_TotalContentBox>
      </_TotalBox>
    </>
  )
}

interface SummaryLineTextProps {
  color?: string
  fontSize?: string
  children: ReactNode
  textDecoration?: string
}

function _SummaryLineText(props: SummaryLineTextProps): JSX.Element {
  return (
    <Text
      component="span"
      variant="header/subheader"
      fontSize={props.fontSize}
      color={props.color || theme.colors.primary.grey}
      textDecoration={props.textDecoration}
    >
      {props.children}
    </Text>
  )
}

const _TotalContentBox = styled(Box)``

const _TotalBox = styled((props: BoxProps & { floating: boolean; stickyBottom?: number }) => {
  const { floating: _floating, stickyBottom: _stickyBottom, ...boxProps } = props
  return <Box {...boxProps} />
})`
  display: block;
  flex: 0 0 auto;
  background-color: white;
  border-top: 1px solid white;
  padding: 16px 34px;
  overflow: hidden;

  ${props =>
    props.stickyBottom !== undefined &&
    css`
      transition: linear 100ms border-color;
      position: sticky;
      z-index: 1;
      bottom: ${props.stickyBottom}px;
      border-top-color: ${props.floating ? theme.colors.secondary.border : 'white'};

      ${_TotalContentBox} {
        transition: linear 100ms transform;
        transform: ${props.floating ? 'scale(1.01)' : 'none'};
      }
    `}
`

function _PlanDowngradeErrorAlert(): JSX.Element {
  return (
    <_TotalBox stickyBottom={0} floating={true} role="alert" aria-live="polite">
      <_TotalContentBox textAlign="center">
        <_SummaryLineText color={theme.colors.status.errorDark}>
          Downgrading through our website is not supported at this time. Please contact{' '}
        </_SummaryLineText>
        <_SummaryLineText color={theme.colors.builder.flowblueDark} textDecoration="underline">
          <Link href="mailto:support@flowcode.com">support@flowcode.com</Link>
        </_SummaryLineText>{' '}
        <_SummaryLineText color={theme.colors.status.errorDark}>
          to downgrade to a lower plan.
        </_SummaryLineText>
      </_TotalContentBox>
    </_TotalBox>
  )
}
